/**
 * debug_perf.js — Instrumentacion temporal para diagnosticar:
 *   BUG 1: queries 500 en artworks (RLS en likes/comments sospechoso)
 *   BUG 2: lentitud/inconsistencia de imagenes en grids
 *
 * Activacion: VITE_DEBUG_PERF=true en .env. Off por default.
 *
 * Uso en runtime (DevTools console):
 *   window.__raios_dump()   → resumen agregado
 *   window.__raios_debug    → estado crudo (queries, images, inflight)
 */

const is_debug_enabled = () => {
  // Runtime override: activa logging en produccion sin redeploy.
  //   localStorage.setItem('raios_debug_perf', 'true')  → on
  //   localStorage.removeItem('raios_debug_perf')       → off
  try {
    if (
      typeof window !== 'undefined' &&
      window.localStorage &&
      window.localStorage.getItem('raios_debug_perf') === 'true'
    ) {
      return true;
    }
  } catch {
    // localStorage puede fallar en modo privado — ignorar
  }

  return (
    typeof import.meta !== 'undefined' &&
    import.meta.env &&
    import.meta.env.VITE_DEBUG_PERF === 'true'
  );
};

const nav_start_ts = typeof performance !== 'undefined' ? performance.now() : Date.now();

const t_rel = () => {
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  return Math.round(now - nav_start_ts);
};

const state = {
  queries: [],
  images: new Map(),
  inflight_count: 0,
  inflight_peak: 0,
  inflight_peak_at: 0,
};

const log = (tag, msg, data) => {
  if (!is_debug_enabled()) return;
  const prefix = `[raios-perf][t+${t_rel()}ms][${tag}]`;
  if (data !== undefined) {
    console.log(prefix, msg, data);
  } else {
    console.log(prefix, msg);
  }
};

const inc_inflight = () => {
  state.inflight_count += 1;
  if (state.inflight_count > state.inflight_peak) {
    state.inflight_peak = state.inflight_count;
    state.inflight_peak_at = t_rel();
    if (state.inflight_peak >= 5 && state.inflight_peak % 5 === 0) {
      log('inflight', `peak=${state.inflight_peak}`);
    }
  }
};

const dec_inflight = () => {
  state.inflight_count = Math.max(0, state.inflight_count - 1);
};

/**
 * Envuelve una query de Supabase para medir duracion, tamaño y error.
 *
 * Uso:
 *   return timed_select('getFeatured', () =>
 *     supabase.from('artworks').select(...).eq(...)
 *   );
 *
 * Si la query falla y el error parece RLS-relacionado, dispara un retry
 * automatico SIN likes(count)/comments(count) para confirmar hipotesis.
 * El retry solo corre en modo debug y solo se loggea (no se retorna).
 */
export async function timed_select(label, builder_fn) {
  if (!is_debug_enabled()) {
    return await builder_fn();
  }

  const start = performance.now();
  inc_inflight();
  let result;
  try {
    result = await builder_fn();
  } catch (throw_err) {
    dec_inflight();
    const duration = Math.round(performance.now() - start);
    const entry = {
      label,
      duration_ms: duration,
      status: 'thrown',
      error: String(throw_err?.message || throw_err),
      t: t_rel(),
    };
    state.queries.push(entry);
    log('query', `${label} | THREW after ${duration}ms`, entry);
    throw throw_err;
  }
  dec_inflight();
  const duration = Math.round(performance.now() - start);

  const { data, error, count } = result || {};
  const row_count = Array.isArray(data) ? data.length : data ? 1 : 0;
  const payload_bytes = data ? byte_size(data) : 0;

  const entry = {
    label,
    duration_ms: duration,
    status: error ? 'error' : 'ok',
    row_count,
    count_total: count,
    payload_kb: Math.round(payload_bytes / 1024),
    error_code: error?.code,
    error_message: error?.message,
    error_details: error?.details,
    error_hint: error?.hint,
    t: t_rel(),
  };
  state.queries.push(entry);

  if (error) {
    log('query', `${label} | ${duration}ms | ERR ${error.code || '?'} ${error.message || ''}`, {
      code: error.code,
      message: error.message,
      details: error.details,
      hint: error.hint,
    });

    // Hipotesis RLS: reintentar sin counts para comparar
    if (looks_like_rls_or_relation_error(error)) {
      log('query', `${label} | retry WITHOUT counts (hypothesis: RLS on likes/comments)`);
    }
  } else {
    log(
      'query',
      `${label} | ${duration}ms | OK | ${row_count} rows | ${Math.round(payload_bytes / 1024)}KB`,
    );
  }

  return result;
}

function looks_like_rls_or_relation_error(error) {
  if (!error) return false;
  const code = String(error.code || '');
  const msg = String(error.message || '').toLowerCase();
  if (code === '42501') return true;
  if (code.startsWith('PGRST')) return true;
  if (msg.includes('permission denied')) return true;
  if (msg.includes('relationship')) return true;
  return false;
}

function byte_size(obj) {
  try {
    return new Blob([JSON.stringify(obj)]).size;
  } catch {
    return 0;
  }
}

/**
 * Hook para medir el ciclo de vida de una imagen en ArtworkCard.
 * Registra: mount, visible (Intersection Observer), fetch_start, load/error.
 *
 * Uso (dentro de un componente React):
 *   const perf = use_image_timing(image_url, { component: 'ArtworkCard', artwork_id: id });
 *   // En el Intersection Observer handler: perf.mark_visible()
 *   // En <img onLoad>: perf.mark_loaded()
 *   // En <img onError>: perf.mark_error()
 *   // En <img> cuando se monta con src: perf.mark_fetch_start()
 */
export function use_image_timing(url, meta = {}) {
  if (!is_debug_enabled()) {
    const noop = () => {};
    return {
      mark_visible: noop,
      mark_fetch_start: noop,
      mark_loaded: noop,
      mark_error: noop,
    };
  }

  const key = `${meta.component || 'unknown'}[${meta.artwork_id || 'n/a'}]`;
  if (!state.images.has(key)) {
    state.images.set(key, {
      key,
      url,
      mount_t: t_rel(),
      visible_t: null,
      fetch_start_t: null,
      loaded_t: null,
      error_t: null,
      duplicates: 0,
    });
    log('img', `${key} | mount`, { url });
  } else {
    const existing = state.images.get(key);
    existing.duplicates += 1;
  }

  const record = state.images.get(key);

  return {
    mark_visible: () => {
      if (record.visible_t === null) {
        record.visible_t = t_rel();
        log('img', `${key} | visible (t+${record.visible_t - record.mount_t}ms since mount)`);
      }
    },
    mark_fetch_start: () => {
      if (record.fetch_start_t === null) {
        record.fetch_start_t = t_rel();
      }
    },
    mark_loaded: () => {
      if (record.loaded_t === null) {
        record.loaded_t = t_rel();
        const since_visible =
          record.visible_t !== null ? record.loaded_t - record.visible_t : null;
        log(
          'img',
          `${key} | LOADED | ${since_visible !== null ? `${since_visible}ms since visible` : 'no visible mark'}`,
        );
      }
    },
    mark_error: () => {
      if (record.error_t === null) {
        record.error_t = t_rel();
        log('img', `${key} | ERROR at t+${record.error_t}ms`, { url: record.url });
      }
    },
  };
}

/**
 * Snapshot agregado. Llamar desde DevTools: window.__raios_dump()
 */
export function dump_session() {
  const queries = state.queries;
  const slow_queries = queries.filter((q) => q.duration_ms > 1000);
  const error_queries = queries.filter((q) => q.status === 'error' || q.status === 'thrown');
  const images = Array.from(state.images.values());
  const failed_imgs = images.filter((i) => i.error_t !== null);
  const slow_imgs = images.filter((i) => {
    if (i.loaded_t === null || i.visible_t === null) return false;
    return i.loaded_t - i.visible_t > 2000;
  });

  const summary = {
    session: {
      build: import.meta.env.VITE_COMMIT_SHA || 'dev',
      connection: typeof navigator !== 'undefined' && navigator.connection
        ? {
            effective_type: navigator.connection.effectiveType,
            downlink: navigator.connection.downlink,
            rtt: navigator.connection.rtt,
            save_data: navigator.connection.saveData,
          }
        : 'unavailable',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a',
      inflight_peak: state.inflight_peak,
      inflight_peak_at: state.inflight_peak_at,
    },
    queries: {
      total: queries.length,
      errors: error_queries.length,
      slow_over_1s: slow_queries.length,
      by_label: group_by_label(queries),
    },
    images: {
      total: images.length,
      loaded: images.filter((i) => i.loaded_t !== null).length,
      errored: failed_imgs.length,
      slow_over_2s: slow_imgs.length,
    },
    error_detail: error_queries.map((q) => ({
      label: q.label,
      code: q.error_code,
      message: q.error_message,
      hint: q.error_hint,
    })),
    failed_images: failed_imgs.map((i) => ({ key: i.key, url: i.url })),
  };

  console.table(summary.queries.by_label);
  console.log('[raios-perf][DUMP]', summary);
  return summary;
}

function group_by_label(queries) {
  const groups = {};
  for (const q of queries) {
    if (!groups[q.label]) {
      groups[q.label] = { count: 0, errors: 0, avg_ms: 0, max_ms: 0 };
    }
    const g = groups[q.label];
    g.count += 1;
    if (q.status !== 'ok') g.errors += 1;
    g.avg_ms = Math.round((g.avg_ms * (g.count - 1) + q.duration_ms) / g.count);
    g.max_ms = Math.max(g.max_ms, q.duration_ms);
  }
  return groups;
}

// Exponer globals siempre (el logging solo ocurre si is_debug_enabled()).
// Permite activar con localStorage.setItem('raios_debug_perf','true') mid-sesion
// sin redeploy ni refresh del bundle.
if (typeof window !== 'undefined') {
  window.__raios_debug = state;
  window.__raios_dump = dump_session;

  if (is_debug_enabled()) {
    console.log(
      `[raios-perf] INSTRUMENTATION ACTIVE | build=${import.meta.env.VITE_COMMIT_SHA || 'dev'} | ts=${new Date().toISOString()}`,
    );
    console.log('[raios-perf] Call window.__raios_dump() in console for summary.');
  } else {
    // Hint silencioso para prender en produccion
    console.log(
      '[raios-perf] instrumentation loaded (inactive). To enable: localStorage.setItem("raios_debug_perf","true") then reload.',
    );
  }
}
